import { useCallback, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  ILocationItemResponse,
} from "../../services/edit-hospital-location/edit-hospital-location.interface";
import {
  NEARBY_AVAILABILITY,
} from "../../services/available-times/nearby-availability.query";
import {
  INearbyAvailabilityResponse,
} from "../../services/available-times/available-times.interface";
import {
  availableTimesMap,
} from "../../services/available-times/available-times.service";
import { getUnixTimestamp } from "../../utils";
import {
  useUrgentVisitStore,
} from "../redux-hooks/urgent-visit-store/urgent-visit.store";
import { IOptionGeneric } from "../../shared/interface";

export interface NearbyLocationWithSlots {
  location: ILocationItemResponse;
  timeSlots: IOptionGeneric[];
  loading: boolean;
}

export const useNearbyHospitalSlots = () => {
  const { selectedPets, homeLocationId, selectedDate } = useUrgentVisitStore();
  const [nearbyLocations, setNearbyLocations] =
    useState<NearbyLocationWithSlots[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const selectedDateEpoch = selectedDate ?? getUnixTimestamp(new Date());

  const [fetchNearbyAvailability] = useLazyQuery<{
    nearbyAvailability: INearbyAvailabilityResponse;
  }>(NEARBY_AVAILABILITY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const entries =
        data?.nearbyAvailability?.otherMetroAvailableTimes ?? [];

      const results: NearbyLocationWithSlots[] = entries
        .map((entry) => {
          const slots = availableTimesMap(
            entry.availableTimes.times,
            entry.timeZone,
          );
          return {
            location: {
              ...(entry.location as unknown as ILocationItemResponse),
              totalDistance: Number(entry.totalDistance || 0),
              timeZone: entry.timeZone,
            },
            timeSlots: slots,
            loading: false,
          };
        })
        .filter((r) => r.timeSlots.length > 0);

      setNearbyLocations(results);
      setLoadingLocations(false);
    },
    onError: () => {
      setLoadingLocations(false);
    },
  });

  const loadNearbyLocations = useCallback(() => {
    if (!homeLocationId) return;

    const pets = selectedPets.map((pet) => ({
      petId: pet.id,
      reasonsForVisit: "",
    }));

    setLoadingLocations(true);
    setNearbyLocations([]);

    fetchNearbyAvailability({
      variables: {
        data: {
          pets,
          date: selectedDateEpoch.toString(),
          locationId: Number(homeLocationId),
        },
      },
    });
  }, [selectedPets, homeLocationId, selectedDateEpoch, fetchNearbyAvailability]);

  return {
    nearbyLocations,
    loadingLocations,
    loadNearbyLocations,
  };
};
